import type { FieldDef, TaskType } from "@/types/content";

import { PRESETS } from "./constants";
import type { FieldPreset, FieldValue, FormValues } from "./types";

export const isPresent = (v: FieldValue): boolean => {
  if (typeof v === "string") return v.trim().length > 0;
  if (typeof v === "number") return !Number.isNaN(v);
  if (typeof v === "boolean") return true;
  return false;
};

const PRESET_NAMESPACE_RE = /presets\.([a-z_]+)/i;

export const getFieldPresets = (field: FieldDef): FieldPreset[] => {
  const namespace = field.placeholder?.match(PRESET_NAMESPACE_RE)?.[1];
  return (namespace && PRESETS[namespace]) || [];
};

export const isTrendTaskType = (t: TaskType) =>
  t.id.startsWith("twin.trends.") ||
  t.id.startsWith("twin.legacy.") ||
  Boolean(t.category);

export const canSubmit = (
  taskType: TaskType | undefined,
  values: FormValues,
): boolean => {
  if (!taskType) return false;

  return taskType.fields.every((field) => {
    if (!field.required) return true;
    if (isPresent(values[field.id])) return true;
    return (field.any_of ?? []).some((altId) => isPresent(values[altId]));
  });
};

export const buildInputs = (taskType: TaskType, values: FormValues) => {
  return taskType.fields.reduce<Record<string, unknown>>((acc, field) => {
    const value = values[field.id];
    if (isPresent(value)) acc[field.id] = value;
    return acc;
  }, {});
};

export const defaultValues = (taskType: TaskType): FormValues => {
  const values: FormValues = {};
  for (const field of taskType.fields) {
    if (field.default == null) continue;
    if (getFieldPresets(field).length > 0) continue;
    values[field.id] = field.default as FieldValue;
  }
  return values;
};

export const buildClusters = (fields: FieldDef[]): Map<string, FieldDef[]> => {
  const clusters = new Map<string, FieldDef[]>();

  for (const field of fields) {
    if (!field.any_of?.length || clusters.has(field.id)) continue;

    const clusterFieldIds = new Set([field.id, ...(field.any_of ?? [])]);
    const clusterFields = fields.filter((candidate) => clusterFieldIds.has(candidate.id));
    if (clusterFields.length <= 1) continue;

    for (const clusterField of clusterFields) {
      clusters.set(clusterField.id, clusterFields);
    }
  }

  return clusters;
};
